import React, { useState, useRef, useEffect } from "react";
import MainModal from "components/ModalMain";
import Button from "components/Button";
import Input from "components/Input";
import Dropdown from "components/Dropdown";
import TextArea from "components/TextArea";
import ModalGeneralSuccessfully from "screens/ModalGeneralSuccessfully";
import ModalAddChargeFailed from "screens/ModalAddChargeFailed";
import ModalGeneralFailed from "screens/ModalGeneralFailed";
import { useMutation } from "@apollo/client";
import { CREATE_CHARGE } from "graphql/service";
import { Head, Container, Footer } from "./styles";
import colors from "constants/colors";

const ModalAddCharge = ({
  hidden,
  buttonStyle,
  bill,
  setBill,
  cardNumber,
  balance,
  bookings,
}) => {

  const inputRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus()
        }
      }, 50)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  const [inputError, setInputError] = useState(false);
  const [chargeAmount, setChargeAmount] = useState("");

  const [chargeType, setChargeType] = useState("");
  const [chargeTypeError, setChargeTypeError] = useState(false);

  const chargeTypeMap = {
    Lodging: "LODGING",
    "Early Check-In": "EARLY_CHECK_IN",
    "Late Check-Out": "LATE_CHECK_OUT",
    Parking: "PARKING",
    "Food & Beverage": "FOOD_BEVERAGE",
    "Room Service": "ROOM_SERVICE",
    Damages: "DAMAGES",
    "Missing Items": "  MISSING_ITEMS",
    "Misc Fees": "MISC_FEES",
  };

  const optionArray =
    bookings?.map((booking) => {
      return {
        label: `Room ${booking.roomBookings?.[0]?.room?.roomNumber}`,
        value: booking.bookingId,
      };
    }) || [];

  const [selectedBookingId, setSelectedBookingId] = useState("");
  const [bookingIdError, setBookingIdError] = useState(false);

  const [chargeDetails, setChargeDetails] = useState("");
  const [textAreaError, setTextAreaError] = useState(false);

  const [chargeSuccessfullyIsOpen, setChargeSuccessfullyIsOpen] = useState(
    false
  );
  const [chargeFailedIsOpen, setChargeFailedIsOpen] = useState(false);
  const [billSuccessfullyIsOpen, setBillSuccessfullyIsOpen] = useState(false);
  const [billFailedIsOpen, setBillFailedIsOpen] = useState(false);

  const [createCharge, { loading }] = useMutation(CREATE_CHARGE);

  const handleAddCharge = () => {
    if (loading) return;
    let error = false;

    if (chargeAmount === "") {
      setInputError(true);
      error = true;
    }

    if (chargeType === "") {
      setChargeTypeError(true);
      error = true;
    }

    if (selectedBookingId === "") {
      setBookingIdError(true);
      error = true;
    }

    if (chargeDetails === "") {
      setTextAreaError(true);
      error = true;
    }

    if (error) return;

    createCharge({
      variables: {
        input: {
          billId: bill.billId,
          type: "EXTRA",
          amount: Number(chargeAmount),
          category: chargeTypeMap[chargeType],
          bookingId: selectedBookingId,
          description: chargeDetails,
        },
      },
    })
      .then((response) => {
        setBill(response.data.createCharge);
        setChargeSuccessfullyIsOpen(true);
        setIsOpen(false);
      })
      .catch((error) => {
        setChargeFailedIsOpen(true);
        setIsOpen(false);
      });
  };

  const handleOnlyAddChargeToBill = () => {
    if (loading) return;
    createCharge({
      variables: {
        input: {
          billId: bill.billId,
          type: "EXTRA",
          amount: Number(chargeAmount),
          description: chargeDetails,
          category: chargeTypeMap[chargeType],
          bookingId: selectedBookingId,
          onlyAddChargeToBill: true,
        },
      },
    })
      .then((response) => {
        setBill(response.data.createCharge);
        setBillSuccessfullyIsOpen(true);
        setChargeFailedIsOpen(false);
      })
      .catch((error) => {
        setBillFailedIsOpen(true);
        setChargeFailedIsOpen(false);
      });
  };

  return (
    <>
      <MainModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        className="modal-success"
        buttonLabel="Add Charge"
        buttonStyle={buttonStyle}
        hidden={hidden}
        additionalstyle={`
          max-width: 500px;
          .modal-content{
            .close {
              z-index: 200;
            }          
            .modal-body {
              height: 100%;
              padding: 24px;
            }
          }
          top: 139px;
        `}
      >
        <Head>Add Charge</Head>
        <Container>
          <div className="inputRow">
            <Input
              inputFieldRef={inputRef}
              title="CHARGE AMOUNT"
              required
              error={inputError}
              errorMessage="Please enter a valid amount"
              value={chargeAmount}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (/^\d*\.?\d{0,2}$/.test(inputValue)) {
                  setChargeAmount(inputValue);
                  setInputError(false);
                }
              }}
              additionalstyle={`
              width: calc(50% - 8px);

              input {
                padding-left: 22px;
              }

              ::before {
                content: '$';
                font-size: 16px;
                font-weight: 600;
                position: absolute;
                color: ${colors.white};
                left: 12px;
                top: 32px;
              }

              .warning {
                white-space: nowrap;
              }
            `}
            />
          </div>
          <div className="inputRow">
            <Dropdown
              title="CHARGE TYPE"
              required
              options={[
                "Lodging",
                "Early Check-In",
                "Late Check-Out",
                "Parking",
                "Food & Beverage",
                "Room Service",
                "Damages",
                "Missing Items",
                "Misc Fees",
              ]}
              value={chargeType}
              placeholder="-Select-"
              onChange={(e) => setChargeType(e)}
              additionalstyle={`
                width: 50%;
              `}
            />
            <Dropdown
              title="APPLY TO ROOM"
              required
              options={optionArray}
              value={
                optionArray.find((o) => o.value === selectedBookingId)?.label ||
                ""
              }
              placeholder="-Select-"
              onChange={(e) => setSelectedBookingId(e)}
              additionalstyle={`
                width: 50%;
              `}
            />
          </div>
          <TextArea
            title="CHARGE DETAILS"
            required
            rows={4}
            error={textAreaError}
            description="Note this appears publicly to the customer on their bill."
            value={chargeDetails}
            onChange={(e) => {
              setChargeDetails(e.target.value);
              setTextAreaError(false);
            }}
            additionalstyle={`
              flex-basis: 100%;
            `}
          />
        </Container>
        <Footer>
          <Button
            success
            additionalstyle={`
              width: 84px;
            `}
            onClick={handleAddCharge}
            data-loading={loading}
          >
            {loading ? null : "Submit"}
          </Button>
        </Footer>
      </MainModal>
      <ModalGeneralSuccessfully
        isOpen={chargeSuccessfullyIsOpen}
        setIsOpen={setChargeSuccessfullyIsOpen}
        amount={chargeAmount}
        cardNumber={cardNumber}
        setAmount={setChargeAmount}
        setDetails={setChargeDetails}
        type="charge"
      />
      <ModalAddChargeFailed
        isOpen={chargeFailedIsOpen}
        setIsOpen={setChargeFailedIsOpen}
        amount={chargeAmount}
        balance={balance}
        loading={loading}
        retryFunction={handleOnlyAddChargeToBill}
      />
      <ModalGeneralSuccessfully
        isOpen={billSuccessfullyIsOpen}
        setIsOpen={setBillSuccessfullyIsOpen}
        amount={chargeAmount}
        setAmount={setChargeAmount}
        setDetails={setChargeDetails}
        type="onlyAddChargeToBill"
      />
      <ModalGeneralFailed
        isOpen={billFailedIsOpen}
        setIsOpen={setBillFailedIsOpen}
        amount={chargeAmount}
        loading={loading}
        retryFunction={handleOnlyAddChargeToBill}
        type="onlyAddChargeToBill"
      />
    </>
  );
};

export default ModalAddCharge;
